/* eslint linebreak-style: ["error", "unix"] */
import { useMutation } from '@apollo/client';
import {
  Button,
  Card,
  Divider, makeStyles, TextField, Typography,
} from '@material-ui/core';
import Spacer from 'components/spacer/spacer';
import { formatGraphqlErrorMessage } from 'libs/graphql/graphql-error-formatters';
import { selectCurrentUser, updateUser } from 'redux/auth.redux';
import { updateUserGql, User } from 'data/users';
import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { emailRequired } from 'utils/validators';
import { yupResolver } from '@hookform/resolvers/yup';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    background: theme.palette.common.white,
    width: '100%',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: theme.spacing(2),

    '& .MuiButton-contained': {
      color: theme.palette.common.white,
      boxShadow: 'none',
    },
  },
  info: {
    padding: theme.spacing(2),
  },
}));

const validationSchema = yup.object().shape({
  email: emailRequired,
});

export default function ChangeEmail() {
  const classes = useStyles();
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const formApi = useForm({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const { handleSubmit, errors, register } = formApi;

  const [mutate, { loading }] = useMutation(
    updateUserGql,
    {
      onError: (error) => {
        enqueueSnackbar(formatGraphqlErrorMessage(error), { variant: 'error' });
      },
      onCompleted: (data) => {
        dispatch(updateUser(data?.updateUser as User));

        enqueueSnackbar('E-mail atualizado com sucesso', { variant: 'success' });
      },
    },
  );

  const onSubmit = (data: Record<string, any>) => {
    if (data?.email === currentUser?.email) {
      enqueueSnackbar('Deve ser um novo e-mail', { variant: 'error' });
      return;
    }

    mutate({
      variables: {
        input: {
          id: currentUser?.id,
          email: data?.email,
        },
      },
    });
  };

  return (
    <div className={classes.container}>
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <Typography color="primary" variant="h6">
          Alterar e-mail
        </Typography>
        <Divider />
        <Spacer spacing={2} />

        <Card square variant="outlined" className={classes.info}>
          <Typography color="primary">
            E-mail atual
          </Typography>
          <Typography>
            {currentUser?.email}
          </Typography>
        </Card>

        <Spacer spacing={2} />

        <TextField
          name="email"
          label="E-mail"
          placeholder="Introduz um novo e-mail"
          type="email"
          inputProps={{
            ref: register,
          }}
          error={!!errors?.email}
          helperText={errors?.email?.message}
        />

        <Spacer spacing={4} />

        <Button type="submit" color="primary" variant="contained" disabled={loading}>
          Alterar e-mail
        </Button>
      </form>
    </div>
  );
}
